import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import type { RootState } from '..';
import { processExportContentAnalyticsJob, processExportLiveChannelsAnalyticsJob, processExportWatchAnalyticsJob } from './processExportContentAnalyticsJob';
import { ExportAnalyticsJob } from './ExportAnalyticsJob';
import { AppAnalyticsState } from '@/pages/AppAnalytics/AppAnalytics';

export type ExportAnalyticsState = {
  arePopupsShowing: boolean;
  jobs: Array<ExportAnalyticsJob>;
  jobType: string;
  batchUploadEnable: boolean;
  ingestInProgress: boolean;
};

export type ExportAnalyticsPayload = {
  jobType: string;
  params: AppAnalyticsState;
};

export const initialState: ExportAnalyticsState = {
  arePopupsShowing: false,
  jobs: [],
  jobType: '',
  batchUploadEnable: false,
  ingestInProgress: false,
}

const exportAnalyticsSlice = createSlice({
  name: 'exportAnalytics',
  initialState,
  reducers: {
    popupsShowing: (state, action) => {
      // console.log('action', action.payload);
      state.arePopupsShowing = action.payload;
    },
    updateBatchUploadEnable: (state, action) => {
      state.batchUploadEnable = action.payload;
    },
    updateIngestInProgress: (state, action) => {
      state.ingestInProgress = action.payload;
    },
    addJob: (state, action) => {
      state.jobs.push(action.payload);
    },
    removeJob: (state, action) => {
      const index = state.jobs.indexOf(action.payload);
      state.jobs.splice(index, 1);
    },
    updateJob: (state, action) => {
      const index = state.jobs.indexOf(action.payload);
      const { jobs } = state;
      jobs.splice(index, 1);
      jobs.push(action.payload);
      state.jobs = jobs;
    },
    clearJobs: (state) => {
      state.jobs = [];
      state.arePopupsShowing = false;
    },
    closePopup: (state, action) => {
      // state.arePopupsShowing = false;
      const index = action.payload;
      const jobs = cloneDeep(state.jobs);
      const newJob = jobs[index];
      newJob.isPopupOpen = false;
      state.jobs = jobs;
    },
  },
  extraReducers: {},
});

export const {
  popupsShowing,
  updateIngestInProgress,
  addJob,
  clearJobs,
  removeJob,
  updateJob,
  closePopup,
  updateBatchUploadEnable,
} = exportAnalyticsSlice.actions;

// export SaveAssetJobsSlice selector to get the slice in any component
export const ExportAnalyticsJobsSelector = (state: RootState) => state.exportAnalyticsJobs;
// export The reducer
const ExportAnalyticsReducer = exportAnalyticsSlice.reducer;
export default ExportAnalyticsReducer;

export const startJob = createAsyncThunk('exportAnalyticsJobs/startJob', async (payload: ExportAnalyticsPayload, { dispatch, getState }) => {
  let toReturn: any = null;
  let state = getState() as RootState;
  let { jobs } = state.exportAnalyticsJobs;
  const job = new ExportAnalyticsJob(payload.jobType, payload.params);
  dispatch(addJob(job));
  dispatch(updateIngestInProgress(true));
  // get latest state
  state = getState() as RootState;

  switch (payload.jobType) {
    case 'Content':
      toReturn = await dispatch(processExportContentAnalyticsJob(job));
      break;
    case 'LiveChannels':
      toReturn = await dispatch(processExportLiveChannelsAnalyticsJob(job));
      break;
    case 'Watch':
      toReturn = await dispatch(processExportWatchAnalyticsJob(job));
      break;
    default:
      break;
  }

  setTimeout(() => {
    state = getState() as RootState;
    jobs = state.exportAnalyticsJobs.jobs;
    jobs.forEach((job) => {
      if (job.jobStatus === 'Completed') {
        dispatch(removeJob(job));
      }
    });
    state = getState() as RootState;
    jobs = state.exportAnalyticsJobs.jobs;
    if (jobs.length === 0) {
      dispatch(clearJobs());
      dispatch(updateIngestInProgress(false));
    }
  }, 1500);

  return toReturn;
});
